import { useState, useEffect, useRef, useMemo, type CSSProperties, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Layers,
  Construction,
  Wrench,
  MapPin,
  FileText,
  Files as FilesIcon,
  Tag,
  Upload,
  Download,
  Pencil,
  Trash2,
  Box,
  ChevronDown,
} from 'lucide-react';
import axios from 'axios';
import { Toast } from '../../components/ui/FormField';
import {
  wellApi,
  companyApi,
  siteApi,
  toolApi,
  annotationApi,
  wellAnnotationApi,
  cloudProcessApi,
} from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type {
  Well,
  Company,
  Site,
  Tool,
  Annotation,
  UploadFileItem,
  GeonomicFileItem,
  CloudProcessItem,
} from '../../types/api';

// ─────────────────────────────────────────────────────────────
// WellFormPage — Document Layout (Option B)
// Full-page Add/Edit Well with a left section TOC + right content
// pane. Sections stack vertically: Identity (required), Operations,
// Technical Parameters, Location & Geology, Notes, Files & Models,
// Annotations.
// ─────────────────────────────────────────────────────────────

/** Pull a human-readable error message from an axios/server error, falling back to a default. */
const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      if (typeof obj.errorMessage === 'string') return obj.errorMessage;
      if (typeof obj.title === 'string') return obj.title;
      if (typeof obj.detail === 'string') return obj.detail;
      if (typeof obj.message === 'string') return obj.message;
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

/** Returns the PNG icon path for a given file extension (matching V2 icons). */
const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const supported = ['avi','csv','doc','exe','jpg','las','mp4','pdf','ply','png','tiff','txt','xls','zip'];
  const icon = supported.includes(ext) ? ext : 'file';
  return `/icons/ico-${icon}.png`;
};

/** Deduplicate LAS files by name, keeping only the latest per filename (matching V2). */
const getLatestLasFiles = (files: GeonomicFileItem[]): GeonomicFileItem[] => {
  const latestByName = new Map<string, GeonomicFileItem>();
  files.forEach((f) => {
    const name = f.fileName?.toLowerCase() ?? '';
    if (!name.endsWith('.las')) return;
    const existing = latestByName.get(name);
    if (!existing || (f.id > existing.id)) {
      latestByName.set(name, f);
    }
  });
  return Array.from(latestByName.values());
};

const emptyWell: Partial<Well> = {
  wellName: '', fieldName: '', frm: '', location: '', county: '', province: '', country: '',
  wellId: '', licenceNumber: '', serviceCompany: '', latitude: '', longitude: '', notes: '',
  afe: '', rig: '', son: '', bs: '', cbl: '', dft: '', dfd: '', dfv: '', tdh: '', tch: '', thbr: '',
  startDepth: 0, stopDepth: 0, perigonSensorOffsetToBit: null,
  companyId: 0, siteId: 0, toolId: null,
};

type SectionKey = 'identity' | 'operations' | 'technical' | 'location' | 'notes' | 'files' | 'annotations';

export function WellFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;

  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const isCompanyAdmin = authUser?.role === 'CompanyAdmin';
  const canSeeInternalDetails = isSuperAdmin || isCompanyAdmin;

  const siteIdParam = Number(searchParams.get('siteId') ?? 0);
  const siteNameParam = searchParams.get('siteName') ?? '';
  const filterCompanyId = Number(searchParams.get('companyId') ?? 0);

  const [form, setForm] = useState<Partial<Well>>(emptyWell);
  const [wellCompanyId, setWellCompanyId] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' } | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionKey>('identity');

  // Annotations
  const [wellAnnotations, setWellAnnotations] = useState<{ id: number; annotationId: number; shortDescription: string; longDescription: string; depth: number }[]>([]);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState('');
  const [annotationDepth, setAnnotationDepth] = useState<number | ''>('');
  const [annotationEditId, setAnnotationEditId] = useState<number | null>(null);

  // Files
  const [uploadedFiles, setUploadedFiles] = useState<UploadFileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const [lasFiles, setLasFiles] = useState<GeonomicFileItem[]>([]);
  const [uploadingLas, setUploadingLas] = useState(false);
  const [lasUploadProgress, setLasUploadProgress] = useState(0);
  const lasFileInputRef = useRef<HTMLInputElement>(null);
  const lasUploadAbortRef = useRef<AbortController | null>(null);
  const [plyFiles, setPlyFiles] = useState<CloudProcessItem[]>([]);
  const [downloadingPly, setDownloadingPly] = useState<string | null>(null);

  // Create well from LAS (create mode only)
  const [creatingFromLas, setCreatingFromLas] = useState(false);
  const createLasFileInputRef = useRef<HTMLInputElement>(null);
  const [isLasDragOver, setIsLasDragOver] = useState(false);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
  };

  // Load reference data
  useEffect(() => {
    companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? [])).catch(() => {});
    toolApi.getAll({ limit: 1000 }).then((r) => setTools(r.entities ?? [])).catch(() => {});
    annotationApi.getAll({ limit: 1000 }).then((r) => setAnnotations(r.entities ?? [])).catch(() => {});
  }, []);

  // Load well for edit
  useEffect(() => {
    if (!isEdit || !id) return;
    wellApi.getById(Number(id))
      .then((r) => {
        const well = r.entity ?? r;
        setForm({ ...well });
        setWellCompanyId((well as Well).companyId ?? 0);
        // Load annotations via dedicated endpoint (getByIdWell doesn't include them)
        wellAnnotationApi.getByWellId(Number(id))
          .then((ar) => {
            setWellAnnotations((ar.entities ?? []).map((a) => ({
              id: a.id,
              annotationId: a.annotationId,
              shortDescription: a.shortDescription ?? '',
              longDescription: a.longDescription ?? '',
              depth: a.depth,
            })));
          })
          .catch(() => {});
        // load files
        wellApi.getWellServiceModel(Number(id))
          .then((fr) => {
            if (fr.status === 'Failed' || fr.error) return;
            const entity = fr.entity;
            const files = entity?.uploadedFiles ?? [];
            const geoFiles = entity?.geonomicFiles ?? [];
            setUploadedFiles(files);
            setLasFiles(getLatestLasFiles(geoFiles.filter((f) => f.geoFileType === 1)));
          })
          .catch(() => showToast('Failed to load files', 'error'));
        // load 3D models
        loadPlyFiles(Number(id));
      })
      .catch(() => showToast('Failed to load well', 'error'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // Load sites when company changes
  useEffect(() => {
    if (form.companyId) {
      siteApi.getAllByCompanyId(Number(form.companyId), { limit: 1000 })
        .then((r) => setSites(r.entities ?? [])).catch(() => {});
    } else {
      setSites([]);
    }
  }, [form.companyId]);

  // Pre-set company/site for new wells
  useEffect(() => {
    if (!isEdit) {
      setForm((prev) => ({
        ...prev,
        companyId: isSuperAdmin ? (filterCompanyId || 0) : Number(authUser?.companyId ?? 0),
        siteId: siteIdParam || 0,
      }));
    }
  }, [isEdit, isSuperAdmin, filterCompanyId, authUser?.companyId, siteIdParam]);

  const set = (key: string, value: string | number | null) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const goBack = () => {
    if (siteIdParam > 0) {
      navigate(`/dashboard/wells?siteId=${siteIdParam}&siteName=${encodeURIComponent(siteNameParam)}`);
    } else {
      navigate('/dashboard/wells');
    }
  };

  // Validation — mirrors V2 well-register required fields
  const errors = {
    companyId: !form.companyId ? 'Company is required' : '',
    siteId: !form.siteId ? 'Location is required' : '',
    wellName: !form.wellName?.trim() ? 'Well Name is required' : '',
    wellId: !form.wellId?.trim() ? 'UWI is required' : '',
    licenceNumber: !form.licenceNumber?.trim() ? 'Licence Number is required' : '',
  };
  const isInvalid = Object.values(errors).some((e) => !!e);

  // Required-field counter for the header chip + sidebar progress
  const requiredFilled = useMemo(() => {
    let n = 0;
    if (form.companyId) n++;
    if (form.siteId) n++;
    if (form.wellName?.trim()) n++;
    if (form.wellId?.trim()) n++;
    if (form.licenceNumber?.trim()) n++;
    return n;
  }, [form.companyId, form.siteId, form.wellName, form.wellId, form.licenceNumber]);
  const requiredTotal = 5;

  const handleSave = async () => {
    setSubmitted(true);
    if (isInvalid) {
      const first = Object.values(errors).find((e) => !!e);
      showToast(first || 'Please fill in all required fields', 'error');
      // jump to identity (where the missing fields live)
      scrollToSection('identity');
      return;
    }
    setSaving(true);
    try {
      const payload = { well: form, GeonomicFiles: [] };
      if (isEdit) {
        await wellApi.update(payload);
        showToast('Well updated successfully');
      } else {
        await wellApi.create(payload);
        showToast('Well created successfully');
      }
      setTimeout(goBack, 800);
    } catch {
      showToast('Failed to save well', 'error');
      setSaving(false);
    }
  };

  // While the user is mid-click (smooth-scrolling to a target), suppress
  // the scroll-spy observer so it doesn't briefly flicker through every
  // section that scrolls past the trigger line. Once the scroll settles,
  // the observer takes over again.
  const suppressSpyUntilRef = useRef(0);

  const scrollToSection = (key: SectionKey) => {
    setActiveSection(key);
    suppressSpyUntilRef.current = Date.now() + 800; // ~smooth-scroll duration
    const el = document.getElementById(`section-${key}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  /**
   * Scroll-spy: highlight the TOC entry for whichever section is
   * currently under the sticky top bar. We use IntersectionObserver
   * with a `rootMargin` that shrinks the viewport from the top by the
   * height of the sticky header (~88px) and from the bottom by ~55%,
   * so a section is considered "active" only while its header crosses
   * the upper trigger band. The topmost intersecting section wins on
   * scroll, with a tiebreak by the section's listed order.
   *
   * Re-runs when isEdit / canSeeInternalDetails change because the set
   * of mounted sections changes with role and mode.
   */
  useEffect(() => {
    if (loading) return; // sections aren't in the DOM yet during initial spinner
    const order: SectionKey[] = ['identity', 'operations', 'technical', 'location', 'notes', 'files', 'annotations'];
    const nodes = order
      .map((k) => ({ key: k, el: document.getElementById(`section-${k}`) }))
      .filter((n): n is { key: SectionKey; el: HTMLElement } => n.el !== null);
    if (nodes.length === 0) return;

    const indexByEl = new Map<Element, number>();
    nodes.forEach((n, i) => indexByEl.set(n.el, i));

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < suppressSpyUntilRef.current) return;
        // Among currently-intersecting sections, pick the one earliest
        // in the document order. Use the cached map to avoid scanning
        // entries again with .find().
        let bestIdx = -1;
        let bestKey: SectionKey | null = null;
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const idx = indexByEl.get(e.target) ?? -1;
          if (idx >= 0 && (bestIdx === -1 || idx < bestIdx)) {
            bestIdx = idx;
            bestKey = nodes[idx].key;
          }
        });
        // Fall back: if nothing intersects this tick (e.g. fast scroll
        // jumped past), do not change activeSection — keep last value.
        if (bestKey) setActiveSection(bestKey);
      },
      {
        // Top: hide the sticky header band so the section under it counts.
        // Bottom: -55% means a section must be in the top ~45% of the
        // viewport to count as active.
        rootMargin: '-88px 0px -55% 0px',
        threshold: 0,
      },
    );

    nodes.forEach((n) => observer.observe(n.el));
    return () => observer.disconnect();
  }, [loading, isEdit, canSeeInternalDetails]);

  // CloudProcess depth range for annotation validation
  const minValidDepth = plyFiles.length > 0 ? Math.min(...plyFiles.map(p => p.fromLength)) : null;
  const maxValidDepth = plyFiles.length > 0 ? Math.max(...plyFiles.map(p => p.toLength)) : null;

  // Annotations helpers
  const addWellAnnotation = async () => {
    if (!selectedAnnotationId || annotationDepth === '' || Number(annotationDepth) <= 0) {
      showToast('Please select an annotation and enter a valid depth', 'error');
      return;
    }
    if (minValidDepth === null || maxValidDepth === null) {
      showToast('Cannot add annotation. This well must have at least one active Cloud Process record.', 'error');
      return;
    }
    if (Number(annotationDepth) < minValidDepth || Number(annotationDepth) > maxValidDepth) {
      showToast(`Depth must be between ${minValidDepth} and ${maxValidDepth} based on Cloud Process records`, 'error');
      return;
    }
    const ann = annotations.find((a) => a.id === Number(selectedAnnotationId));
    if (!ann) return;
    if (annotationEditId !== null) {
      try {
        await wellAnnotationApi.update({
          id: annotationEditId,
          wellId: Number(id),
          annotationId: Number(selectedAnnotationId),
          depth: Number(annotationDepth),
          shortDescription: ann.shortDescription,
          longDescription: ann.longDescription,
        });
        setWellAnnotations((prev) => prev.map((wa) => wa.id === annotationEditId
          ? { ...wa, annotationId: Number(selectedAnnotationId), shortDescription: ann.shortDescription, longDescription: ann.longDescription, depth: Number(annotationDepth) }
          : wa));
        setAnnotationEditId(null);
      } catch {
        showToast('Failed to update annotation', 'error');
      }
    } else {
      try {
        const result = await wellAnnotationApi.add({
          wellId: Number(id),
          annotationId: Number(selectedAnnotationId),
          depth: Number(annotationDepth),
          shortDescription: ann.shortDescription,
          longDescription: ann.longDescription,
        });
        const newId = result.entity?.id ?? Date.now();
        setWellAnnotations((prev) => [...prev, { id: newId, annotationId: Number(selectedAnnotationId), shortDescription: ann.shortDescription, longDescription: ann.longDescription, depth: Number(annotationDepth) }]);
      } catch {
        showToast('Failed to add annotation', 'error');
      }
    }
    setSelectedAnnotationId('');
    setAnnotationDepth('');
  };

  const removeWellAnnotation = async (wid: number) => {
    try {
      await wellAnnotationApi.delete(wid);
      setWellAnnotations((prev) => prev.filter((wa) => wa.id !== wid));
    } catch {
      showToast('Failed to delete annotation', 'error');
    }
  };

  const editWellAnnotation = (wid: number) => {
    const wa = wellAnnotations.find((a) => a.id === wid);
    if (!wa) return;
    setSelectedAnnotationId(String(wa.annotationId));
    setAnnotationDepth(wa.depth);
    setAnnotationEditId(wid);
  };

  // File helpers — used by upload handlers to refresh file lists after
  // upload/delete operations. Initial load is handled in the main
  // useEffect with generation guarding.
  const loadFiles = (wellId: number) => {
    wellApi.getWellServiceModel(wellId)
      .then((r) => {
        if (r.status === 'Failed' || r.error) {
          showToast('Failed to refresh file list', 'error');
          return;
        }
        const entity = r.entity;
        const files = entity?.uploadedFiles ?? [];
        const geoFiles = entity?.geonomicFiles ?? [];
        setUploadedFiles(files);
        setLasFiles(getLatestLasFiles(geoFiles.filter((f) => f.geoFileType === 1)));
      })
      .catch(() => showToast('Failed to refresh file list', 'error'));
  };

  const loadPlyFiles = (wellId: number) => {
    cloudProcessApi.getAllByWellId(wellId)
      .then((r) => {
        if (r.status === 'Failed' || r.error) return;
        setPlyFiles((r.entities ?? []).filter((p) => !p.isDeleted));
      }).catch(() => {});
  };

  const open3DModel = (file: CloudProcessItem) => {
    const token = localStorage.getItem('geo_access_token') || '';
    const viewerBase = import.meta.env.VITE_3D_VIEWER_URL || 'https://gviewtest.geonomictech.com';
    const url = `${viewerBase}/?fileId=${file.id}&wellId=${file.wellId}&token=${token}`;
    window.open(url, '_blank');
  };

  const handlePlyDownload = async (file: CloudProcessItem, cloudType: 'A' | 'P') => {
    const suffix = cloudType === 'A' ? 'Actual' : 'Perfect';
    const fileName = `${file.wellName}_${file.fromLength}m-${file.toLength}m_${suffix}_${file.id}.ply`;
    setDownloadingPly(fileName);
    try {
      const blob = await cloudProcessApi.downloadPlyFile(file.id, cloudType);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast(`Failed to download ${fileName}`, 'error');
    } finally {
      setDownloadingPly(null);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !isEdit) return;
    const cId = wellCompanyId || Number(form.companyId);
    if (!cId) { showToast('Company ID not loaded yet — please wait', 'error'); return; }
    setUploading(true);
    setUploadProgress(0);
    const abort = new AbortController();
    uploadAbortRef.current = abort;
    try {
      await wellApi.uploadClientFileChunked(
        file, cId, Number(id),
        (pct) => setUploadProgress(pct),
        abort.signal,
      );
      showToast(`${file.name} uploaded successfully`);
      loadFiles(Number(id));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        showToast('Upload cancelled', 'error');
      } else {
        showToast('Failed to upload file', 'error');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      uploadAbortRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (fileName: string) => {
    const cId = wellCompanyId || Number(form.companyId);
    setDownloadingFile(fileName);
    try {
      const blob = await wellApi.downloadFile(fileName, cId, Number(id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Failed to download file', 'error');
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleDeleteFile = async (fileName: string) => {
    const cId = wellCompanyId || Number(form.companyId);
    if (!confirm(`Delete file "${fileName}"?`)) return;
    try {
      await wellApi.deleteFile(fileName, cId, Number(id));
      showToast(`${fileName} deleted`);
      loadFiles(Number(id));
    } catch {
      showToast('Failed to delete file', 'error');
    }
  };

  const handleCreateWellFromLas = async (file: File | undefined | null) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.las')) {
      showToast('Please select a valid .las file', 'error');
      return;
    }
    const cId = Number(form.companyId);
    if (!cId) { showToast('Please select a Company first', 'error'); return; }
    const sId = Number(form.siteId) || siteIdParam || 0;
    setCreatingFromLas(true);
    try {
      const result = await wellApi.uploadLas(file, cId, sId);
      if (result?.status === 'Successfull' && (result.entities?.length ?? 0) > 0) {
        showToast(`${file.name} uploaded — well(s) created`);
        setTimeout(goBack, 800);
      } else {
        showToast(result?.error?.errorMessage || 'Failed to create well from LAS', 'error');
      }
    } catch (err) {
      showToast(extractErrorMessage(err, 'Failed to upload LAS file'), 'error');
    } finally {
      setCreatingFromLas(false);
      if (createLasFileInputRef.current) createLasFileInputRef.current.value = '';
    }
  };

  const handleLasUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !isEdit) return;
    const cId = wellCompanyId || Number(form.companyId);
    setUploadingLas(true);
    setLasUploadProgress(0);
    const abort = new AbortController();
    lasUploadAbortRef.current = abort;
    try {
      await wellApi.uploadLasForWell(
        file, Number(id), cId,
        (pct) => setLasUploadProgress(pct),
        abort.signal,
      );
      showToast(`${file.name} uploaded successfully`);
      loadFiles(Number(id));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        showToast('Upload cancelled', 'error');
      } else {
        showToast(extractErrorMessage(err, 'Failed to upload LAS file'), 'error');
      }
    } finally {
      setUploadingLas(false);
      setLasUploadProgress(0);
      lasUploadAbortRef.current = null;
      if (lasFileInputRef.current) lasFileInputRef.current.value = '';
    }
  };

  const handleLasDelete = async (f: GeonomicFileItem) => {
    if (!confirm(`Delete LAS file "${f.fileName}"?`)) return;
    try {
      await wellApi.update({
        well: form,
        GeonomicFiles: [{ id: f.id, isDeleted: true, geoFileType: 1, fileName: f.fileName, fileType: f.fileType, contentType: f.contentType, companyId: f.companyId }],
      });
      showToast(`${f.fileName} deleted`);
      loadFiles(Number(id));
    } catch {
      showToast('Failed to delete LAS file', 'error');
    }
  };

  const handleLasDownload = async (f: { id: number; fileName: string }) => {
    const cId = wellCompanyId || Number(form.companyId);
    setDownloadingFile(f.fileName);
    try {
      const blob = await wellApi.downloadLasFile(f.id, cId, Number(id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = f.fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Failed to download LAS file', 'error');
    } finally {
      setDownloadingFile(null);
    }
  };

  // ── Sections meta for TOC ───────────────────────────────────
  const sections: { key: SectionKey; label: string; required?: boolean; show: boolean }[] = [
    { key: 'identity',    label: 'Identity',    required: true,  show: true },
    { key: 'operations',  label: 'Operations',  show: true },
    { key: 'technical',   label: 'Technical',   show: canSeeInternalDetails },
    { key: 'location',    label: 'Location',    show: canSeeInternalDetails },
    { key: 'notes',       label: 'Notes',       show: true },
    { key: 'files',       label: 'Files',       show: isEdit },
    { key: 'annotations', label: 'Annotations', show: isEdit && canSeeInternalDetails },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '96px 0' }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          border: '4px solid var(--color-accent)',
          borderTopColor: 'transparent',
          animation: 'spin 1s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Top header bar ──────────────────────────────────── */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
          boxShadow: '0 2px 6px rgba(15,23,32,0.04)',
        }}
      >
        <BackButton onClick={goBack} />
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: 'var(--color-text-dim)',
              fontFamily: 'var(--font-mono)',
              fontWeight: 500,
            }}
          >
            Wells · {isEdit ? 'Editing record' : 'New record'}
            {siteIdParam > 0 && siteNameParam && (
              <> · <span style={{ color: 'var(--color-text-mid)' }}>{siteNameParam}</span></>
            )}
          </div>
          <div
            style={{
              fontSize: 19,
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
              color: 'var(--color-text)',
              letterSpacing: -0.2,
              marginTop: 2,
            }}
          >
            {isEdit ? 'Edit Well' : 'Add Well'}
          </div>
        </div>

        <RequiredChip filled={requiredFilled} total={requiredTotal} />

        <SecondaryButton onClick={goBack}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleSave} disabled={saving}>
          <Check size={13} /> {saving ? 'Saving…' : 'Save Well'}
        </PrimaryButton>
      </div>

      {/* ── Two-column body ─────────────────────────────────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 240px) 1fr',
          gap: 18,
          alignItems: 'flex-start',
        }}
      >
        {/* Left — Section TOC */}
        <aside
          style={{
            position: 'sticky',
            top: 88,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: 'var(--color-text-dim)',
              fontFamily: 'var(--font-mono)',
              fontWeight: 500,
              padding: '0 4px',
            }}
          >
            Sections
          </div>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sections.filter((s) => s.show).map((s) => (
              <TocLink
                key={s.key}
                label={s.label}
                required={s.required}
                active={activeSection === s.key}
                onClick={() => scrollToSection(s.key)}
              />
            ))}
          </nav>

          {/* Required progress */}
          <div
            style={{
              marginTop: 4,
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span
                style={{
                  fontSize: 11,
                  letterSpacing: 0.4,
                  color: 'var(--color-text-mid)',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                Required
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: requiredFilled === requiredTotal ? 'var(--color-teal)' : 'var(--color-text)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {requiredFilled} / {requiredTotal}
              </span>
            </div>
            <div
              style={{
                height: 4,
                borderRadius: 3,
                background: 'var(--color-border)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(requiredFilled / requiredTotal) * 100}%`,
                  background: requiredFilled === requiredTotal ? 'var(--color-teal)' : 'var(--color-accent)',
                  transition: 'width 200ms ease, background 200ms ease',
                }}
              />
            </div>
          </div>
        </aside>

        {/* Right — Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          {/* ── Identity ── */}
          <Section
            id="section-identity"
            icon={<Layers size={17} />}
            title="Well Identity"
            subtitle="Core identification — required to save"
            required
          >
            <Row>
              <Field label="Company" required error={submitted ? errors.companyId : ''}>
                <SelectBox value={form.companyId ?? ''} onChange={(e) => set('companyId', Number(e.target.value))}>
                  <option value="">Select company…</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                </SelectBox>
              </Field>
              <Field
                label="Surface Location"
                required
                hint="(filtered by company)"
                error={submitted ? errors.siteId : ''}
              >
                <SelectBox
                  value={form.siteId ?? ''}
                  onChange={(e) => set('siteId', Number(e.target.value))}
                  disabled={!form.companyId}
                >
                  <option value="">{form.companyId ? 'Select location…' : 'Select company first'}</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.siteName}</option>)}
                </SelectBox>
              </Field>
            </Row>
            <Row cols={3}>
              <Field label="Well Name" required error={submitted ? errors.wellName : ''}>
                <InputBox
                  value={form.wellName ?? ''}
                  onChange={(e) => set('wellName', e.target.value)}
                  placeholder="e.g. ARCRES HZ INGA E11-25-84-24W6"
                />
              </Field>
              <Field label="UWI" required error={submitted ? errors.wellId : ''}>
                <InputBox
                  value={form.wellId ?? ''}
                  onChange={(e) => set('wellId', e.target.value)}
                  placeholder="e.g. 100/13-35-084-24W6/00"
                />
              </Field>
              <Field label="Licence Number" required error={submitted ? errors.licenceNumber : ''}>
                <InputBox
                  value={form.licenceNumber ?? ''}
                  onChange={(e) => set('licenceNumber', e.target.value)}
                  placeholder="0512345"
                />
              </Field>
            </Row>
            <Row>
              <Field label="Field Name">
                <InputBox
                  value={form.fieldName ?? ''}
                  onChange={(e) => set('fieldName', e.target.value)}
                  placeholder="e.g. Inga"
                />
              </Field>
              <Field label="AFE">
                <InputBox
                  value={form.afe ?? ''}
                  onChange={(e) => set('afe', e.target.value)}
                  placeholder="Authorization for Expenditure #"
                />
              </Field>
            </Row>
          </Section>

          {/* ── Operations ── */}
          <Section
            id="section-operations"
            icon={<Construction size={17} />}
            title="Operations"
            subtitle="Rig assignment and service scheduling"
            optional
          >
            <Row>
              <Field label="Rig">
                <InputBox value={form.rig ?? ''} onChange={(e) => set('rig', e.target.value)} placeholder="e.g. Rig 67" />
              </Field>
              <Field label="GTI Job Number">
                <InputBox value={form.son ?? ''} onChange={(e) => set('son', e.target.value)} placeholder="J016" />
              </Field>
            </Row>
            <Row>
              <Field label="Service Mobilization Date">
                <InputBox
                  type="date"
                  value={form.serviceDate?.split('T')[0] ?? ''}
                  onChange={(e) => set('serviceDate', e.target.value)}
                />
              </Field>
              <Field label="Service Release Date">
                <InputBox
                  type="date"
                  value={form.serviceReleaseDate?.split('T')[0] ?? ''}
                  onChange={(e) => set('serviceReleaseDate', e.target.value)}
                />
              </Field>
            </Row>
          </Section>

          {/* ── Technical Parameters ── */}
          {canSeeInternalDetails && (
            <Section
              id="section-technical"
              icon={<Wrench size={17} />}
              title="Technical Parameters"
              subtitle="Tool, bit, depth, drilling fluid, and hours"
              optional
              internalOnly
            >
              <Row>
                <Field label="Perigon Tool Used">
                  <SelectBox value={form.toolId ?? ''} onChange={(e) => set('toolId', e.target.value ? Number(e.target.value) : null)}>
                    <option value="">Select a tool ({tools.length} available)</option>
                    {tools.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.toolModel?.model || t.toolModel?.toolModelName || 'Unknown'} — SN: {t.serialNumber}
                      </option>
                    ))}
                  </SelectBox>
                </Field>
                <Field label="Bit Size" hint="(mm)">
                  <SelectBox value={form.bs ?? ''} onChange={(e) => set('bs', e.target.value)}>
                    <option value="">Select size…</option>
                    {['156', '159', '171', '200', '216', '222'].map((v) => <option key={v} value={v}>{v}</option>)}
                  </SelectBox>
                </Field>
              </Row>
              <Row cols={3}>
                <Field label="Start Bit Depth" hint="(meters)">
                  <InputBox
                    type="number"
                    value={form.startDepth ?? ''}
                    onChange={(e) => set('startDepth', Number(e.target.value))}
                    placeholder="0"
                  />
                </Field>
                <Field label="End Bit Depth" hint="(meters)">
                  <InputBox
                    type="number"
                    value={form.stopDepth ?? ''}
                    onChange={(e) => set('stopDepth', Number(e.target.value))}
                    placeholder="0"
                  />
                </Field>
                <Field label="Perigon Sensor Offset to Bit">
                  <InputBox
                    type="number"
                    value={form.perigonSensorOffsetToBit ?? ''}
                    onChange={(e) => set('perigonSensorOffsetToBit', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder="Enter offset"
                  />
                </Field>
              </Row>
              <Row cols={3}>
                <Field label="Drilling Fluid Type">
                  <SelectBox value={form.dft ?? ''} onChange={(e) => set('dft', e.target.value)}>
                    <option value="">Select type…</option>
                    <option value="Water Based">Water Based</option>
                    <option value="Oil Based">Oil Based</option>
                    <option value="Brine">Brine</option>
                  </SelectBox>
                </Field>
                <Field label="Drilling Fluid Density" hint="(kg/L)">
                  <InputBox value={form.dfd ?? ''} onChange={(e) => set('dfd', e.target.value)} placeholder="1.2" />
                </Field>
                <Field label="Drilling Fluid Viscosity" hint="(sec)">
                  <InputBox value={form.dfv ?? ''} onChange={(e) => set('dfv', e.target.value)} placeholder="45" />
                </Field>
              </Row>
              <Row cols={3}>
                <Field label="Total Drilling Hours">
                  <InputBox type="number" value={form.tdh ?? ''} onChange={(e) => set('tdh', e.target.value)} placeholder="0" />
                </Field>
                <Field label="Total Circulating Hours">
                  <InputBox type="number" value={form.tch ?? ''} onChange={(e) => set('tch', e.target.value)} placeholder="0" />
                </Field>
                <Field label="Total Hours Below Rotary">
                  <InputBox type="number" value={form.thbr ?? ''} onChange={(e) => set('thbr', e.target.value)} placeholder="0" />
                </Field>
              </Row>
            </Section>
          )}

          {/* ── Location & Geology ── */}
          {canSeeInternalDetails && (
            <Section
              id="section-location"
              icon={<MapPin size={17} />}
              title="Location & Geology"
              subtitle="GPS coordinates and geological context"
              optional
              internalOnly
            >
              <Row>
                <Field label="Intermediate Casing Point (ICP)">
                  <InputBox value={form.cbl ?? ''} onChange={(e) => set('cbl', e.target.value)} placeholder="1850m" />
                </Field>
                <Field label="Geological Formation">
                  <InputBox value={form.frm ?? ''} onChange={(e) => set('frm', e.target.value)} placeholder="e.g. Montney, Duvernay" />
                </Field>
              </Row>
              <Row>
                <Field label="Latitude">
                  <InputBox value={form.latitude ?? ''} onChange={(e) => set('latitude', e.target.value)} placeholder="56.2345" />
                </Field>
                <Field label="Longitude">
                  <InputBox value={form.longitude ?? ''} onChange={(e) => set('longitude', e.target.value)} placeholder="-120.5678" />
                </Field>
              </Row>
            </Section>
          )}

          {/* ── Notes ── */}
          <Section
            id="section-notes"
            icon={<FileText size={17} />}
            title="Notes"
            subtitle="Additional context or observations"
            optional
          >
            <TextAreaBox
              rows={5}
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Enter any additional notes, observations, or context for this well record…"
            />
          </Section>

          {/* ── Files & Models ── */}
          {isEdit && (
            <Section
              id="section-files"
              icon={<FilesIcon size={17} />}
              title="Files & Models"
              subtitle="Upload 3D models, LAS files, and client documents"
              optional
            >
              {/* 3D Models */}
              {canSeeInternalDetails && (
                <FileGroup title="3D Models" badge="Internal Only">
                  {plyFiles.length === 0 ? (
                    <EmptyHint>No 3D models uploaded</EmptyHint>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {plyFiles.map((f) => {
                        const actualName = `${f.wellName}_${f.fromLength}m-${f.toLength}m_Actual_${f.id}.ply`;
                        const perfectName = `${f.wellName}_${f.fromLength}m-${f.toLength}m_Perfect_${f.id}.ply`;
                        return (
                          <div key={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <FileRow name={actualName} icon="/icons/ico-ply.png">
                              <FileAction title="Open 3D Viewer" onClick={() => open3DModel(f)} variant="info">
                                <Box size={14} />
                              </FileAction>
                              <FileAction title="Download" onClick={() => handlePlyDownload(f, 'A')} disabled={downloadingPly !== null} variant="good">
                                <Download size={14} />
                              </FileAction>
                            </FileRow>
                            <FileRow name={perfectName} icon="/icons/ico-ply.png">
                              <FileAction title="Open 3D Viewer" onClick={() => open3DModel(f)} variant="info">
                                <Box size={14} />
                              </FileAction>
                              <FileAction title="Download" onClick={() => handlePlyDownload(f, 'P')} disabled={downloadingPly !== null} variant="good">
                                <Download size={14} />
                              </FileAction>
                            </FileRow>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </FileGroup>
              )}

              {/* Client Downloads */}
              <FileGroup
                title="Client Downloads"
                badge="Customer Viewable"
                action={canSeeInternalDetails ? (
                  <>
                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
                    <UploadButton onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      <Upload size={12} /> {uploading ? 'Uploading…' : 'Upload File'}
                    </UploadButton>
                  </>
                ) : undefined}
              >
                {uploading && (
                  <UploadProgress
                    pct={uploadProgress}
                    onCancel={() => uploadAbortRef.current?.abort()}
                  />
                )}
                {uploadedFiles.length === 0 && !uploading ? (
                  <EmptyHint>No client files uploaded</EmptyHint>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {uploadedFiles.map((f) => (
                      <FileRow key={f.fileName} name={f.fileName} icon={getFileIcon(f.fileName)}>
                        <FileAction title="Download" onClick={() => handleDownload(f.fileName)} disabled={downloadingFile === f.fileName} variant="good">
                          <Download size={14} />
                        </FileAction>
                        {canSeeInternalDetails && (
                          <FileAction title="Delete" onClick={() => handleDeleteFile(f.fileName)} variant="bad">
                            <Trash2 size={14} />
                          </FileAction>
                        )}
                      </FileRow>
                    ))}
                  </div>
                )}
              </FileGroup>

              {/* LAS Files */}
              {canSeeInternalDetails && (
                <FileGroup
                  title="LAS Files"
                  badge="Internal Only"
                  action={(
                    <>
                      <input ref={lasFileInputRef} type="file" accept=".las" style={{ display: 'none' }} onChange={handleLasUpload} />
                      <UploadButton onClick={() => lasFileInputRef.current?.click()} disabled={uploadingLas}>
                        <Upload size={12} /> {uploadingLas ? 'Uploading…' : 'Upload LAS'}
                      </UploadButton>
                    </>
                  )}
                >
                  {uploadingLas && (
                    <UploadProgress
                      pct={lasUploadProgress}
                      onCancel={() => lasUploadAbortRef.current?.abort()}
                    />
                  )}
                  {lasFiles.length === 0 && !uploadingLas ? (
                    <EmptyHint>No LAS files uploaded</EmptyHint>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {lasFiles.map((f) => (
                        <FileRow key={f.id} name={f.fileName} icon="/icons/ico-las.png">
                          <FileAction title="Download LAS" onClick={() => handleLasDownload(f)} disabled={downloadingFile === f.fileName} variant="good">
                            <Download size={14} />
                          </FileAction>
                          <FileAction title="Delete LAS" onClick={() => handleLasDelete(f)} variant="bad">
                            <Trash2 size={14} />
                          </FileAction>
                        </FileRow>
                      ))}
                    </div>
                  )}
                </FileGroup>
              )}
            </Section>
          )}

          {/* ── Create well from LAS (create mode only) ── */}
          {!isEdit && (
            <Section
              id="section-files"
              icon={<FilesIcon size={17} />}
              title="Create Well from LAS"
              subtitle="Upload a LAS file to auto-populate well details"
              optional
              internalOnly
            >
              <input
                ref={createLasFileInputRef}
                type="file"
                accept=".las"
                style={{ display: 'none' }}
                onChange={(e) => handleCreateWellFromLas(e.target.files?.[0])}
              />
              <div
                onClick={() => {
                  if (creatingFromLas) return;
                  if (!form.companyId) { showToast('Please select a Company first', 'error'); return; }
                  createLasFileInputRef.current?.click();
                }}
                onDragOver={(e) => { e.preventDefault(); if (!creatingFromLas) setIsLasDragOver(true); }}
                onDragLeave={() => setIsLasDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsLasDragOver(false);
                  if (creatingFromLas) return;
                  const file = e.dataTransfer.files?.[0];
                  handleCreateWellFromLas(file);
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '28px 18px',
                  borderRadius: 8,
                  border: `1.5px dashed ${isLasDragOver ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  background: isLasDragOver ? 'rgba(11,74,111,0.04)' : 'var(--color-panel-alt)',
                  cursor: creatingFromLas ? 'not-allowed' : 'pointer',
                  opacity: creatingFromLas ? 0.6 : 1,
                  transition: 'border-color 150ms, background 150ms',
                }}
              >
                <Upload size={22} style={{ color: 'var(--color-text-dim)' }} />
                <span style={{ fontSize: 13, color: 'var(--color-text-mid)' }}>
                  {creatingFromLas
                    ? 'Uploading LAS file…'
                    : (form.companyId ? 'Drag and drop your LAS file here, or click to browse' : 'Select a Company first')}
                </span>
              </div>
            </Section>
          )}

          {/* ── Annotations ── */}
          {isEdit && canSeeInternalDetails && (
            <Section
              id="section-annotations"
              icon={<Tag size={17} />}
              title="Annotations"
              subtitle="Depth markers and reference points"
              optional
              internalOnly
            >
              <Row cols={3}>
                <Field label="Annotation">
                  <SelectBox value={selectedAnnotationId} onChange={(e) => setSelectedAnnotationId(e.target.value)}>
                    <option value="">Select an annotation</option>
                    {annotations.map((a) => <option key={a.id} value={a.id}>{a.shortDescription}</option>)}
                  </SelectBox>
                </Field>
                <Field label="Depth">
                  <InputBox
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={annotationDepth}
                    onChange={(e) => setAnnotationDepth(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="Enter depth"
                  />
                </Field>
                <Field label={annotationEditId !== null ? 'Update existing' : 'Add new'}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <PrimaryButton
                      onClick={addWellAnnotation}
                      disabled={!selectedAnnotationId || annotationDepth === '' || Number(annotationDepth) <= 0}
                    >
                      {annotationEditId !== null ? 'Update' : 'Add'}
                    </PrimaryButton>
                    {annotationEditId !== null && (
                      <SecondaryButton onClick={() => { setAnnotationEditId(null); setSelectedAnnotationId(''); setAnnotationDepth(''); }}>
                        Cancel
                      </SecondaryButton>
                    )}
                  </div>
                </Field>
              </Row>

              {/* List */}
              <div
                style={{
                  marginTop: 6,
                  borderRadius: 8,
                  border: '1px solid var(--color-border)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(160px,1.2fr) minmax(220px,2fr) 110px 96px',
                    padding: '10px 14px',
                    background: 'var(--color-panel-alt)',
                    fontSize: 11.5,
                    letterSpacing: 0.4,
                    color: 'var(--color-text-mid)',
                    fontWeight: 600,
                    fontFamily: 'var(--font-body)',
                    borderBottom: '1px solid var(--color-border)',
                  }}
                >
                  <div>Annotation</div>
                  <div>Description</div>
                  <div>Depth</div>
                  <div style={{ textAlign: 'right' }}>Actions</div>
                </div>
                {wellAnnotations.length === 0 ? (
                  <div style={{ padding: 22, textAlign: 'center', fontSize: 13, color: 'var(--color-text-dim)' }}>
                    No annotations added
                  </div>
                ) : wellAnnotations.map((wa, i) => (
                  <div
                    key={wa.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(160px,1.2fr) minmax(220px,2fr) 110px 96px',
                      padding: '11px 14px',
                      borderBottom: i === wellAnnotations.length - 1 ? 'none' : '1px solid var(--color-border)',
                      fontSize: 13.5,
                      alignItems: 'center',
                      color: 'var(--color-text)',
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{wa.shortDescription}</div>
                    <div style={{ color: 'var(--color-text-mid)' }}>{wa.longDescription}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{wa.depth}</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                      <FileAction title="Edit" onClick={() => editWellAnnotation(wa.id)} variant="info">
                        <Pencil size={13} />
                      </FileAction>
                      <FileAction title="Delete" onClick={() => removeWellAnnotation(wa.id)} variant="bad">
                        <Trash2 size={13} />
                      </FileAction>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────────────────────

function BackButton({ onClick }: { onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="Back to Wells"
      style={{
        width: 34,
        height: 34,
        borderRadius: 7,
        border: `1px solid ${hover ? 'var(--color-accent)' : 'var(--color-border)'}`,
        background: hover ? 'rgba(11,74,111,0.06)' : 'var(--color-panel)',
        color: hover ? 'var(--color-accent)' : 'var(--color-text-mid)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 120ms',
      }}
    >
      <ArrowLeft size={16} />
    </button>
  );
}

function RequiredChip({ filled, total }: { filled: number; total: number }) {
  const complete = filled === total;
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 10px',
        borderRadius: 999,
        background: complete ? 'rgba(21,128,141,0.10)' : 'rgba(183,121,31,0.10)',
        border: `1px solid ${complete ? 'rgba(21,128,141,0.30)' : 'rgba(183,121,31,0.30)'}`,
        color: complete ? 'var(--color-teal)' : 'var(--color-warn)',
        fontSize: 11.5,
        fontWeight: 600,
        fontFamily: 'var(--font-mono)',
        letterSpacing: 0.3,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 3,
          background: complete ? 'var(--color-teal)' : 'var(--color-warn)',
        }}
      />
      {complete ? `${total} / ${total} required` : `${total - filled} required field${total - filled === 1 ? '' : 's'}`}
    </div>
  );
}

// Buttons — sizing matches PrimaryButton / GhostButton in
// components/ui/Field.tsx (padding 10/18, fontSize 13.5, radius 8)
// so they pair visually with the buttons in the Companies / Users /
// Locations forms.
function PrimaryButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        background: disabled
          ? 'var(--color-border)'
          : hover ? 'var(--color-accent-hover)' : 'var(--color-accent)',
        color: disabled ? 'var(--color-text-dim)' : '#fff',
        border: 'none',
        padding: '10px 18px',
        borderRadius: 8,
        fontSize: 13.5,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        color: 'var(--color-text-mid)',
        border: '1px solid var(--color-border)',
        padding: '10px 18px',
        borderRadius: 8,
        fontSize: 13.5,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: 'pointer',
        transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );
}

function TocLink({
  label,
  required,
  active,
  onClick,
}: {
  label: string;
  required?: boolean;
  active: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        textAlign: 'left',
        padding: '7px 12px',
        borderRadius: 6,
        border: 'none',
        background: active ? 'rgba(11,74,111,0.08)' : hover ? 'var(--color-panel-alt)' : 'transparent',
        color: active ? 'var(--color-accent)' : 'var(--color-text)',
        fontWeight: active ? 600 : 500,
        fontSize: 13,
        fontFamily: 'var(--font-body)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        transition: 'all 120ms',
        borderLeft: active ? '2px solid var(--color-accent)' : '2px solid transparent',
        paddingLeft: 10,
      }}
    >
      <span>{label}</span>
      {required && (
        <span style={{ color: 'var(--color-bad)', fontWeight: 700, fontSize: 12 }}>*</span>
      )}
    </button>
  );
}

function Section({
  id,
  icon,
  title,
  subtitle,
  required,
  optional,
  internalOnly,
  children,
}: {
  id: string;
  icon: ReactNode;
  title: string;
  subtitle: string;
  required?: boolean;
  optional?: boolean;
  internalOnly?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        overflow: 'hidden',
        scrollMarginTop: 88,
      }}
    >
      {/* Section header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 18px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-panel-alt)',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 7,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-accent)',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3
              style={{
                margin: 0,
                fontSize: 14.5,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
                color: 'var(--color-text)',
                letterSpacing: -0.1,
              }}
            >
              {title}
            </h3>
            {required && <Pill color="bad">Required</Pill>}
            {optional && <Pill color="dim">Optional</Pill>}
            {internalOnly && <Pill color="warn">Internal Only</Pill>}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--color-text-dim)',
              marginTop: 2,
            }}
          >
            {subtitle}
          </div>
        </div>
      </div>
      {/* Section body */}
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {children}
      </div>
    </section>
  );
}

function Pill({ children, color }: { children: ReactNode; color: 'bad' | 'warn' | 'dim' | 'teal' }) {
  const palette: Record<typeof color, { bg: string; fg: string; bd: string }> = {
    bad:  { bg: 'rgba(185,28,28,0.10)',  fg: 'var(--color-bad)',   bd: 'rgba(185,28,28,0.25)' },
    warn: { bg: 'rgba(183,121,31,0.10)', fg: 'var(--color-warn)',  bd: 'rgba(183,121,31,0.28)' },
    dim:  { bg: 'var(--color-panel)',    fg: 'var(--color-text-dim)', bd: 'var(--color-border)' },
    teal: { bg: 'rgba(21,128,141,0.10)', fg: 'var(--color-teal)',  bd: 'rgba(21,128,141,0.28)' },
  };
  const c = palette[color];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 7px',
        borderRadius: 4,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.bd}`,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        fontFamily: 'var(--font-mono)',
      }}
    >
      {children}
    </span>
  );
}

function Row({ children, cols = 2 }: { children: ReactNode; cols?: 2 | 3 }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: 14,
      }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Form primitives — typography aligned with components/ui/Field.tsx
// so the Add/Edit Well page matches Companies/Locations/Users forms
// (same label size, same input height/padding/radius, same focus ring).
// The only intentional divergence is that `hint` here is rendered
// inline next to the label (matches the Document Layout design),
// whereas Field.tsx renders hint below the input.
// ─────────────────────────────────────────────────────────────

function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span
        style={{
          fontSize: 13.5,
          color: 'var(--color-text-mid)',
          fontWeight: 500,
          fontFamily: 'var(--font-body)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        {label}
        {required && (
          <span style={{ color: 'var(--color-bad)', fontSize: 11, marginTop: -2 }}>*</span>
        )}
        {hint && (
          <span style={{ color: 'var(--color-text-dim)', fontWeight: 400, fontSize: 11.5 }}>
            {hint}
          </span>
        )}
      </span>
      {children}
      {error && (
        <span style={{ fontSize: 11.5, color: 'var(--color-bad)', lineHeight: 1.3 }}>{error}</span>
      )}
    </label>
  );
}

// Shared control styling — matches `baseControl` in components/ui/Field.tsx
const inputBaseStyle: CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  fontSize: 13.5,
  fontFamily: 'var(--font-body)',
  color: 'var(--color-text)',
  background: 'var(--color-panel)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  outline: 'none',
  transition: 'border-color 120ms, box-shadow 120ms',
  boxSizing: 'border-box',
};

const FOCUS_SHADOW = '0 0 0 3px rgba(58,184,207,0.18)';

function InputBox(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
      onBlur={(e)  => { setFocused(false); props.onBlur?.(e); }}
      style={{
        ...inputBaseStyle,
        borderColor: focused ? 'var(--color-accent-bright)' : 'var(--color-border)',
        boxShadow: focused ? FOCUS_SHADOW : 'none',
        ...(props.style ?? {}),
      }}
    />
  );
}

function SelectBox({ children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <select
        {...props}
        onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
        onBlur={(e)  => { setFocused(false); props.onBlur?.(e); }}
        style={{
          ...inputBaseStyle,
          paddingRight: 34,
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          cursor: props.disabled ? 'not-allowed' : 'pointer',
          opacity: props.disabled ? 0.6 : 1,
          borderColor: focused ? 'var(--color-accent-bright)' : 'var(--color-border)',
          boxShadow: focused ? FOCUS_SHADOW : 'none',
          ...(props.style ?? {}),
        }}
      >
        {children}
      </select>
      <ChevronDown
        size={14}
        style={{
          position: 'absolute',
          right: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--color-text-dim)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

function TextAreaBox(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      {...props}
      onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
      onBlur={(e)  => { setFocused(false); props.onBlur?.(e); }}
      style={{
        width: '100%',
        padding: '10px 12px',
        fontSize: 13.5,
        fontFamily: 'var(--font-body)',
        color: 'var(--color-text)',
        background: 'var(--color-panel)',
        border: '1px solid',
        borderColor: focused ? 'var(--color-accent-bright)' : 'var(--color-border)',
        borderRadius: 8,
        outline: 'none',
        resize: 'vertical',
        lineHeight: 1.5,
        minHeight: 90,
        boxShadow: focused ? FOCUS_SHADOW : 'none',
        transition: 'border-color 120ms, box-shadow 120ms',
        boxSizing: 'border-box',
        ...(props.style ?? {}),
      }}
    />
  );
}

function FileGroup({
  title,
  badge,
  action,
  children,
}: {
  title: string;
  badge?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          background: 'var(--color-panel-alt)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--color-text)',
            }}
          >
            {title}
          </span>
          {badge && <Pill color={badge === 'Customer Viewable' ? 'teal' : 'warn'}>{badge}</Pill>}
        </div>
        {action}
      </div>
      <div style={{ padding: 12 }}>{children}</div>
    </div>
  );
}

function FileRow({ name, icon, children }: { name: string; icon: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        background: 'var(--color-panel-alt)',
        border: '1px solid var(--color-border)',
        borderRadius: 6,
      }}
    >
      <img src={icon} alt="" style={{ width: 30, height: 30, flexShrink: 0 }} />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          color: 'var(--color-text)',
          wordBreak: 'break-all',
        }}
      >
        {name}
      </span>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>{children}</div>
    </div>
  );
}

function FileAction({
  children,
  title,
  onClick,
  disabled,
  variant,
}: {
  children: ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  variant: 'info' | 'good' | 'bad';
}) {
  const [hover, setHover] = useState(false);
  const palette = {
    info: { idle: 'var(--color-accent)',  hover: '#0d567f', hoverBg: 'rgba(11,74,111,0.10)' },
    good: { idle: 'var(--color-teal)',    hover: '#0f6671', hoverBg: 'rgba(21,128,141,0.10)' },
    bad:  { idle: '#a14a47',              hover: 'var(--color-bad)', hoverBg: 'rgba(185,28,28,0.10)' },
  } as const;
  const c = palette[variant];
  const active = hover && !disabled;
  return (
    <button
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 28,
        height: 28,
        borderRadius: 6,
        border: `1px solid ${active ? c.hover : 'var(--color-border)'}`,
        background: active ? c.hoverBg : 'var(--color-panel)',
        color: active ? c.hover : c.idle,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 120ms',
      }}
    >
      {children}
    </button>
  );
}

function UploadButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 10px',
        background: hover && !disabled ? 'rgba(11,74,111,0.08)' : 'var(--color-panel)',
        color: 'var(--color-accent)',
        border: `1px solid ${hover && !disabled ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 120ms',
      }}
    >
      {children}
    </button>
  );
}

function UploadProgress({ pct, onCancel }: { pct: number; onCancel: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--color-border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--color-accent)', transition: 'width 200ms' }} />
      </div>
      <span style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--color-text-mid)', width: 38, textAlign: 'right' }}>
        {pct}%
      </span>
      <button
        onClick={onCancel}
        style={{
          fontSize: 11.5,
          color: 'var(--color-bad)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-body)',
        }}
        title="Cancel upload"
      >
        Cancel
      </button>
    </div>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: '18px 12px',
        textAlign: 'center',
        fontSize: 12.5,
        color: 'var(--color-text-dim)',
      }}
    >
      {children}
    </div>
  );
}
